// components/dashboard/widgets/PlagioIA.jsx
import React from 'react';
import PropTypes from 'prop-types';
import { Spin, Empty, List, Tag, Progress, Alert } from 'antd';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ComposedChart
} from 'recharts';
import {
  ExclamationCircleOutlined,
  WarningOutlined,
  EyeOutlined
} from '@ant-design/icons';
import { useDashboardData } from '../../../hooks/useDashboardData';

const PlagioIA = ({ endpoint, filtros, formato }) => {
  const { dados, loading, error } = useDashboardData(endpoint, filtros);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 40 }}>
        <Spin size="large" />
        <p style={{ marginTop: 16, color: '#666' }}>Carregando métricas de plágio e IA...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          padding: '20px',
          textAlign: 'center',
          backgroundColor: '#fff2f0',
          border: '1px solid #ffccc7',
          borderRadius: '6px',
          color: '#a8071a'
        }}
      >
        <p style={{ margin: '0 0 4px 0', fontWeight: 'bold' }}>
          Erro ao carregar métricas de plágio e IA
        </p>
        <small style={{ color: '#cf1322' }}>{error?.message || String(error)}</small>
      </div>
    );
  }

  if (!dados || dados.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: 40, color: '#52c41a' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
        <h4 style={{ color: '#52c41a', margin: 0 }}>Nenhuma irregularidade detectada!</h4>
        <p style={{ color: '#8c8c8c', margin: '8px 0 0 0' }}>
          Todos os trabalhos estão dentro dos parâmetros aceitáveis.
        </p>
      </div>
    );
  }

  // --- Helpers ---------------------------------------------------------------

  const getRiscoNivel = (plagioPercent, iaPercent) => {
    const plagio = parseFloat(plagioPercent) || 0;
    const ia = parseFloat(iaPercent) || 0;
    if (plagio >= 70 || ia >= 80) return 'critico';
    if (plagio >= 50 || ia >= 60) return 'alto';
    if (plagio >= 30 || ia >= 40) return 'medio';
    if (plagio >= 15 || ia >= 20) return 'baixo';
    return 'aceitavel';
  };

  const getCor = (risco) => {
    const cores = {
      critico: '#f5222d',
      alto: '#fa541c',
      medio: '#fa8c16',
      baixo: '#fadb14',
      aceitavel: '#52c41a'
    };
    return cores[risco] || '#8c8c8c';
  };

  const getIcone = (risco) => {
    if (risco === 'critico') return <ExclamationCircleOutlined />;
    if (risco === 'alto' || risco === 'medio') return <WarningOutlined />;
    return <EyeOutlined />;
  };

  const formatarData = (data) => {
    if (!data) return 'Não informada';
    return new Date(data).toLocaleDateString('pt-BR');
  };

  // --- Processamento ---------------------------------------------------------

  const dadosProcessados = dados
    .map((item) => {
      const plagioPercent =
        parseFloat(item.plagio_percentual) || parseFloat(item.percentual_plagio) || 0;
      const iaPercent =
        parseFloat(item.ia_percentual) || parseFloat(item.percentual_ia) || 0;
      const estudanteNome = item.estudante || item.nome_estudante || 'Não informado';
      const documento = item.documento || item.titulo || item.arquivo || 'Documento sem nome';

      return {
        estudante: estudanteNome,
        documento: documento.length > 40 ? `${documento.substring(0, 40)}...` : documento,
        documento_completo: documento,
        plagio_percentual: plagioPercent,
        ia_percentual: iaPercent,
        curso: item.curso || 'Não informado',
        tipo_documento: item.tipo_documento || item.tipo || 'Monografia',
        data_verificacao: item.data_verificacao || item.data_analise || new Date().toISOString(),
        risco: getRiscoNivel(plagioPercent, iaPercent),
        risco_combinado: plagioPercent + iaPercent,
        fonte_plagio: item.fonte_plagio || item.fontes || 'Não especificada',
        modelo_ia: item.modelo_ia || item.detector_ia || 'Detector padrão'
      };
    })
    .sort((a, b) => b.risco_combinado - a.risco_combinado);

  const estatisticas = {
    total: dadosProcessados.length,
    criticos: dadosProcessados.filter((i) => i.risco === 'critico').length,
    altos: dadosProcessados.filter((i) => i.risco === 'alto').length,
    mediaPlagio:
      dadosProcessados.length > 0
        ? dadosProcessados.reduce((s, i) => s + i.plagio_percentual, 0) / dadosProcessados.length
        : 0,
    mediaIA:
      dadosProcessados.length > 0
        ? dadosProcessados.reduce((s, i) => s + i.ia_percentual, 0) / dadosProcessados.length
        : 0,
    maiorPlagio:
      dadosProcessados.length > 0
        ? Math.max(...dadosProcessados.map((i) => i.plagio_percentual))
        : 0,
    maiorIA:
      dadosProcessados.length > 0
        ? Math.max(...dadosProcessados.map((i) => i.ia_percentual))
        : 0
  };

  // --- Tooltip com PropTypes para o lint ------------------------------------

  const CustomTooltip = ({ active, payload /*, label*/ }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const plagio = typeof data.plagio_percentual === 'number' ? data.plagio_percentual : 0;
      const ia = typeof data.ia_percentual === 'number' ? data.ia_percentual : 0;

      return (
        <div
          style={{
            backgroundColor: '#fff',
            padding: '12px',
            border: '1px solid #d9d9d9',
            borderRadius: '6px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            minWidth: '250px'
          }}
        >
          <p style={{ margin: 0, fontWeight: 'bold', marginBottom: 8 }}>👤 {data.estudante}</p>
          <div style={{ fontSize: 12 }}>
            <p style={{ margin: '4px 0', color: '#1890ff' }}>📄 {data.documento}</p>
            <p style={{ margin: '4px 0', color: '#f5222d' }}>
              🔍 Plágio: <strong>{plagio.toFixed(1)}%</strong>
            </p>
            <p style={{ margin: '4px 0', color: '#fa8c16' }}>
              🤖 IA: <strong>{ia.toFixed(1)}%</strong>
            </p>
            <p style={{ margin: '4px 0', color: '#666' }}>🏫 Curso: {data.curso}</p>
            <p style={{ margin: '4px 0', color: '#666' }}>📋 Tipo: {data.tipo_documento}</p>
            <div
              style={{
                marginTop: 8,
                padding: '4px 8px',
                borderRadius: '4px',
                backgroundColor: `${getCor(data.risco)}20`,
                color: getCor(data.risco),
                textAlign: 'center',
                fontSize: 11,
                fontWeight: 'bold'
              }}
            >
              {String(data.risco).toUpperCase()}
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  CustomTooltip.propTypes = {
    active: PropTypes.bool,
    payload: PropTypes.arrayOf(
      PropTypes.shape({
        payload: PropTypes.shape({
          estudante: PropTypes.string,
          documento: PropTypes.string,
          plagio_percentual: PropTypes.number,
          ia_percentual: PropTypes.number,
          curso: PropTypes.string,
          tipo_documento: PropTypes.string,
          risco: PropTypes.string
        })
      })
    ),
    label: PropTypes.any
  };

  // --- Renderizações por formato --------------------------------------------

  if (formato === 'lista') {
    return (
      <div style={{ height: 350, overflowY: 'auto' }}>
        {estatisticas.criticos > 0 && (
          <Alert
            message={`${estatisticas.criticos} documentos com risco crítico`}
            description="Investigação imediata necessária para possível plágio ou uso de IA."
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 8,
            marginBottom: 16,
            padding: '8px',
            backgroundColor: '#fafafa',
            borderRadius: '4px',
            fontSize: 11
          }}
        >
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontWeight: 'bold', color: '#f5222d' }}>{estatisticas.total}</div>
            <div style={{ color: '#666' }}>Documentos</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontWeight: 'bold', color: '#fa541c' }}>{estatisticas.criticos}</div>
            <div style={{ color: '#666' }}>Críticos</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontWeight: 'bold', color: '#1890ff' }}>
              {estatisticas.mediaPlagio.toFixed(1)}%
            </div>
            <div style={{ color: '#666' }}>Plágio Médio</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontWeight: 'bold', color: '#fa8c16' }}>
              {estatisticas.mediaIA.toFixed(1)}%
            </div>
            <div style={{ color: '#666' }}>IA Médio</div>
          </div>
        </div>

        <List
          dataSource={dadosProcessados.slice(0, 15)}
          renderItem={(item) => (
            <List.Item
              style={{
                padding: '12px',
                borderBottom: '1px solid #f0f0f0',
                backgroundColor: item.risco === 'critico' ? '#fff2f0' : 'transparent'
              }}
            >
              <List.Item.Meta
                avatar={
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      backgroundColor: getCor(item.risco),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: 16
                    }}
                  >
                    {getIcone(item.risco)}
                  </div>
                }
                title={
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 'bold', fontSize: 13 }}>{item.estudante}</span>
                    <Tag color="red" style={{ fontSize: 10 }}>
                      🔍 {item.plagio_percentual.toFixed(1)}%
                    </Tag>
                    <Tag color="orange" style={{ fontSize: 10 }}>
                      🤖 {item.ia_percentual.toFixed(1)}%
                    </Tag>
                    <Tag color={getCor(item.risco)} style={{ fontSize: 10 }}>
                      {item.risco.toUpperCase()}
                    </Tag>
                  </div>
                }
                description={
                  <div style={{ fontSize: 12 }}>
                    <div style={{ fontWeight: 'bold', color: '#1890ff', marginBottom: 4 }}>
                      📄 {item.documento}
                    </div>
                    <div style={{ color: '#666', marginBottom: 4 }}>
                      🏫 {item.curso} • 📋 {item.tipo_documento}
                    </div>
                    <div style={{ color: '#8c8c8c', fontSize: 11 }}>
                      📅 Verificado: {formatarData(item.data_verificacao)}
                    </div>
                    {item.fonte_plagio && item.fonte_plagio !== 'Não especificada' && (
                      <div style={{ color: '#8c8c8c', fontSize: 11 }}>🔗 Fonte: {item.fonte_plagio}</div>
                    )}
                  </div>
                }
              />
              <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', gap: 4 }}>
                <Progress
                  percent={item.plagio_percentual}
                  size="small"
                  strokeColor="#f5222d"
                  format={(percent) => `${percent.toFixed(1)}%`}
                  style={{ width: 100 }}
                />
                <Progress
                  percent={item.ia_percentual}
                  size="small"
                  strokeColor="#fa8c16"
                  format={(percent) => `${percent.toFixed(1)}%`}
                  style={{ width: 100 }}
                />
              </div>
            </List.Item>
          )}
        />
      </div>
    );
  }

  // Formato combinado (padrão)
  return (
    <div style={{ height: 350 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-around',
          marginBottom: 16,
          padding: '8px',
          backgroundColor: '#fafafa',
          borderRadius: '4px',
          fontSize: 12
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontWeight: 'bold', color: '#f5222d' }}>
            {estatisticas.mediaPlagio.toFixed(1)}%
          </div>
          <div style={{ color: '#666' }}>Média Plágio</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontWeight: 'bold', color: '#fa8c16' }}>
            {estatisticas.mediaIA.toFixed(1)}%
          </div>
          <div style={{ color: '#666' }}>Média IA</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontWeight: 'bold', color: '#fa541c' }}>{estatisticas.criticos}</div>
          <div style={{ color: '#666' }}>Críticos</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontWeight: 'bold', color: '#1890ff' }}>{estatisticas.total}</div>
          <div style={{ color: '#666' }}>Total</div>
        </div>
      </div>

      <div style={{ width: '100%', height: 'calc(350px - 110px)' }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={dadosProcessados.slice(0, 10)}
            margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="estudante"
              tick={{ fontSize: 10 }}
              stroke="#666"
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis
              tick={{ fontSize: 12 }}
              stroke="#666"
              label={{ value: 'Percentual (%)', angle: -90, position: 'insideLeft' }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="plagio_percentual" fill="#f5222d" name="Plágio %" radius={[4, 4, 0, 0]} />
            <Bar dataKey="ia_percentual" fill="#fa8c16" name="IA %" radius={[4, 4, 0, 0]} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div
        style={{
          marginTop: 8,
          fontSize: 11,
          color: '#666',
          display: 'flex',
          justifyContent: 'center',
          gap: 12,
          flexWrap: 'wrap'
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#f5222d' }} />
          Plágio
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#fa8c16' }} />
          IA
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#f5222d' }} />
          Crítico (≥70% ou ≥80%)
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#52c41a' }} />
          Aceitável (&lt;15% e &lt;20%)
        </span>
      </div>
    </div>
  );
};

PlagioIA.displayName = 'PlagioIA';
PlagioIA.propTypes = {
  endpoint: PropTypes.string.isRequired,
  filtros: PropTypes.object,
  formato: PropTypes.oneOf(['grafico', 'lista', 'combinado'])
};

PlagioIA.defaultProps = {
  filtros: {},
  formato: 'combinado'
};

export default PlagioIA;
